import React, { useState, useEffect } from "react";
import SEO from "../seo";

const DigitalProduct = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "get-started",
        "key-features",
        "example-workflow",
        "product-options",
        "add-options",
        "generate-variants",
        "benefits-variants",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Digital Products | xPage Help Center"
        description="Learn how to create, manage, and securely deliver digital products with xPage. Sell e-books, templates, videos, audio, design files, and more with instant delivery and protected download links."
        keywords="xPage, digital products, instant delivery, secure downloads, downloadable products, digital files, Shopify integration, AI product page"
        canonical="https://help.xpage.ai/help/store-operation/Digital%20Product"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Digital Products
          </h1>
          <p className="text-sm text-systemLabel">
            xPage lets you sell digital items such as e-books, templates, music,
            videos, design files, courses, and downloadable assets. Digital
            products are delivered instantly to customers after a successful
            payment, with no shipping or fulfillment steps required. With
            xPage’s intuitive editor and instant delivery engine, you can
            create, manage, and deliver digital products securely — all without
            technical knowledge.
          </p>
        </header>

        {/* Get Started Section */}
        <section id="get-started" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Get started with digital product creation
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When accessing the Products page, you can add a digital product
              the same way as any other item — with two setup paths:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Option</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Generate
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Paste a product URL or upload reference images, and xPage
                    will generate your complete digital product page
                    automatically, including copy, visuals, and layout.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Create manually
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Add all details yourself: title, description, pricing, and
                    upload your digital files.
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="text-sm text-systemLabel">
              After choosing an option, you're guided through standard setup
              steps for product details — title, description, pricing — plus the
              digital-specific step of uploading the files your customers will
              receive after purchase.
            </p>
          </div>
        </section>

        {/* Key Features Section */}
        <section id="key-features" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Key features and benefits
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Feature</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Instant file delivery
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Customers receive secure download links immediately after
                    payment. No shipping, no waiting.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Secure file protection
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Files are stored privately and delivered through
                    time-limited, tokenized links to prevent unauthorized
                    sharing.
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Multiple file support
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Upload one or many files (PDFs, ZIPs, videos, audio, fonts,
                    3D assets, and more).
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Access control
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Downloads deactivate automatically for refunded or canceled
                    orders.
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      AI-generated product pages
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Generate a full digital product landing page optimized for
                    conversions using a single link, prompt, or image.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Shopify integration
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Import the product page directly into your Shopify store
                    with one click.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Example Workflow Section */}
        <section id="example-workflow" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Example workflow
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              In the xPage dashboard, click Generate to let AI create the
              product page for you — or choose manual creation.
            </p>

            <p className="text-sm text-systemLabel mb-4">
              Add your product information: title, description, pricing,
              imagery.
            </p>

            <ol className="list-decimal list-inside space-y-2 text-sm text-systemLabel ml-4">
              <li>
                Upload the digital files your customers will download (PDF, ZIP,
                video, audio, etc.).
              </li>
              <li>Customize the layout and content using the editor.</li>
              <li>Preview your product page and publish it.</li>
              <li>
                After payment, customers automatically receive secure download
                links by email and on the order confirmation page.
              </li>
            </ol>
          </div>
        </section>

        {/* Uploading Files Section */}
        <section id="product-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Uploading your digital files
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              xPage supports all major digital asset formats, including:
            </p>

            <p className="text-sm text-systemLabel mb-4">
              PDF, PNG, JPG, MP4, MP3, WAV, ZIP, RAR, 7Z, EPUB, MOBI, PSD, AI,
              OBJ, STL, OTF, TTF, WOFF, and more.
            </p>

            <p className="text-sm text-systemLabel mb-3">You can upload:</p>

            <ul className="list-disc text-sm text-systemLabel space-y-2 ml-6 mb-4">
              <li>a single file (e.g., one PDF)</li>
              <li>multiple files (e.g., PDF + ZIP + video)</li>
              <li>
                updated files at any time (new orders get the newest version)
              </li>
            </ul>

            <p className="text-sm text-systemLabel">
              xPage automatically handles storage, secure access, and download
              protection.
            </p>
          </div>
        </section>

        {/* Delivery Section */}
        <section id="add-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Delivery & customer experience
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              xPage delivers digital products immediately after payment success.
            </p>

            <p className="text-sm text-systemLabel mb-4">
              There are no shipping or delivery statuses for digital products.
            </p>

            <p className="text-sm text-systemLabel mb-3 font-medium">
              Customers receive:
            </p>

            <ul className="list-disc text-sm text-systemLabel space-y-2 ml-6 mb-4">
              <li>A secure download link on the thank-you page</li>
              <li>A link on their order status page</li>
              <li>A download email containing time-limited, protected URLs</li>
            </ul>

            <p className="text-sm text-systemLabel mb-3 font-medium">
              Access is automatically disabled if:
            </p>

            <ul className="list-disc text-sm text-systemLabel space-y-2 ml-6 mb-4">
              <li>The order is refunded</li>
              <li>The order is canceled</li>
              <li>
                The download limit or expiration time is reached (if configured)
              </li>
            </ul>

            <p className="text-sm text-systemLabel">
              There are no “Shipped” or “Delivered” states — digital files are
              fulfilled instantly.
            </p>
          </div>
        </section>

        {/* Managing Digital Products Section */}
        <section id="generate-variants" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Managing digital products
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              You can update or manage digital files at any time:
            </p>

            <ul className="list-disc text-sm text-systemLabel space-y-2 ml-6 mb-4">
              <li>Upload new versions</li>
              <li>Add additional files</li>
              <li>Remove or replace files</li>
              <li>Resend the download email</li>
              <li>Disable download access for specific orders</li>
            </ul>

            <p className="text-sm text-systemLabel">
              xPage keeps previous orders secure and maintains tokenized access
              per order.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="benefits-variants" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Benefits of selling digital products with xPage
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Benefit</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Instant fulfillment
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    No logistics or shipping — buyers receive their product
                    instantly.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Higher margins
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Zero shipping cost and no physical inventory.
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Global delivery
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Digital files are accessible worldwide instantly.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Security
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Smart download protection reduces piracy and link sharing.
                  </td>
                </tr>
                <tr>
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      AI-powered creation
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Create and launch digital products 10× faster with automated
                    landing pages and content.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Digital Products
              </a>
            </li>
            <li>
              <a
                href="#get-started"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "get-started"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Get started with digital product creation
              </a>
            </li>
            <li>
              <a
                href="#key-features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key features and benefits
              </a>
            </li>
            <li>
              <a
                href="#example-workflow"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "example-workflow"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Example workflow
              </a>
            </li>
            <li>
              <a
                href="#product-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "product-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Uploading your digital files
              </a>
            </li>
            <li>
              <a
                href="#add-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "add-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Delivery & customer experience
              </a>
            </li>
            <li>
              <a
                href="#generate-variants"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "generate-variants"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Managing digital products
              </a>
            </li>
            <li>
              <a
                href="#benefits-variants"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "benefits-variants"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Benefits of selling digital products with xPage
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DigitalProduct;